import { bunja } from 'bunja';
import { createScopeFromContext, useBunja } from 'bunja/react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { createContext } from 'react';

import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { fuzzyScore } from '@flipster/utils/fuzzy';

import { devtoolsRouteBunja } from '../../../state';
import { dagDevtoolsBunja } from './state';

export const BunjaList = () => {
  const { searchTextAtom, searchResultAtom } = useBunja(bunjaListBunja);
  const searchResult = useAtomValue(searchResultAtom);
  const setSearchText = useSetAtom(searchTextAtom);

  return (
    <div
      className={flex({
        direction: 'column',
        w: '300px',
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        borderColor: 'border.default.primary',
        overflow: 'hidden',
      })}
    >
      <div
        className={flex({
          p: 2,
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'border.default.level-1',
          bgColor: 'background.default.level-1',
        })}
      >
        <input
          type='text'
          placeholder='Search bunjas...'
          onChange={(e) => setSearchText(e.target.value)}
          className={css({
            w: '100%',
            px: 8,
            py: 4,
            rounded: 4,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.default.level-2',
            bgColor: 'background.default.level-0',
            color: 'inverse.white',
            textStyle: 'Text/S/Monospace/Regular',
            outline: 'none',
            transition: 'all 0.15s ease',
            _focus: { borderColor: 'border.accent.info' },
            _placeholder: { color: 'content.default.level-3' },
          })}
        />
      </div>
      <div className={flex({ direction: 'column', gap: 1, p: 2, flex: 1, overflow: 'auto' })}>
        {searchResult.map(([id, bunja]) => (
          <BunjaItemContext key={id} value={id}>
            <BunjaItem id={id} debugLabel={bunja.debugLabel} />
          </BunjaItemContext>
        ))}
        {searchResult.length === 0 && (
          <div
            className={flex({
              p: 4,
              justifyContent: 'center',
              color: 'content.default.level-3',
              textStyle: 'Text/M/Regular',
            })}
          >
            No bunjas found
          </div>
        )}
      </div>
    </div>
  );
};

const bunjaListBunja = bunja(() => {
  const { bunjasAtom } = bunja.use(dagDevtoolsBunja);
  const bunjaEntriesAtom = atom((get) => Object.entries(get(bunjasAtom)));

  const searchTextAtom = atom('');
  const searchResultAtom = atom((get) => {
    const searchText = get(searchTextAtom).toLowerCase().trim();
    const bunjaEntries = get(bunjaEntriesAtom);
    if (!searchText) return bunjaEntries;
    return bunjaEntries
      .map(([id, bunja]) => {
        const targets = [id, bunja.debugLabel || 'Anonymous Bunja'];
        const score = targets
          .map((target) => fuzzyScore(searchText, target.toLowerCase()))
          .sort((a, b) => b.compare(a))
          .at(0)!;
        return { id, bunja, score };
      })
      .filter((item) => item.score.match > 0)
      .sort((a, b) => b.score.compare(a.score))
      .map(({ id, bunja }) => [id, bunja] as [string, typeof bunja]);
  });
  return { searchTextAtom, searchResultAtom };
});
bunjaListBunja.debugLabel = 'bunjaListBunja';

const BunjaItemContext = createContext<string>(null!);
const BunjaItemScope = createScopeFromContext(BunjaItemContext);

interface BunjaItemProps {
  id: string;
  debugLabel?: string;
}

const BunjaItem = ({ id, debugLabel }: BunjaItemProps) => {
  const { isSelectedAtom, select } = useBunja(bunjaItemBunja);
  const isSelected = useAtomValue(isSelectedAtom);

  return (
    <button
      onClick={select}
      className={css({
        width: '100%',
        p: 2,
        rounded: 4,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        border: 'none',
        bg: isSelected ? 'background.accent.info' : 'transparent',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        _hover: { bg: isSelected ? 'background.accent.info' : 'background.default.level-3' },
      })}
    >
      <div
        className={flex({
          textStyle: 'Text/M/Monospace/Regular',
          color: isSelected ? 'content.accent.info' : 'content.default.level-3',
          minW: '30px',
          justifyContent: 'flex-end',
        })}
      >
        {id}
      </div>
      <div
        className={flex({
          textStyle: 'Text/M/Monospace/Regular',
          color: isSelected ? 'content.accent.info' : 'inverse.white',
        })}
      >
        {debugLabel || 'Anonymous Bunja'}
      </div>
    </button>
  );
};

const bunjaItemBunja = bunja(() => {
  const { selectedBunjaIdAtom } = bunja.use(dagDevtoolsBunja);
  const { changeTab } = bunja.use(devtoolsRouteBunja);
  const bunjaId = bunja.use(BunjaItemScope);

  const isSelectedAtom = atom((get) => get(selectedBunjaIdAtom) === bunjaId);
  const select = () => changeTab('dag', { selectedBunjaId: bunjaId });

  return { isSelectedAtom, select };
});
bunjaItemBunja.debugLabel = 'bunjaItemBunja';
